import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { useMappedKeydown } from '../../hooks/useMappedKeydown';
import '../../styles/EPGTimeline.css';

export interface EPGProgram {
  id: string | number;
  title: string;
  start: string;
  end: string;
  type?: string;
}

export interface EPGChannel {
  id: string | number;
  name: string;
  logo: string;
  logoColor: string;
  programs: EPGProgram[];
}

export interface EPGTimelineProps {
  channels: EPGChannel[];
}

export const EPGTimeline: React.FC<EPGTimelineProps> = ({ channels }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [focusedProgram, setFocusedProgram] = useState({
    channelIndex: 0,
    programIndex: 0,
  });
  const [scrollOffset, setScrollOffset] = useState(0);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Generate time slots (10am to 2pm)
  const generateTimeSlots = () => {
    const slots = [];
    const start = new Date();
    start.setHours(10, 0, 0, 0);
    for (let i = 0; i < 240; i += 30) {
      const time = new Date(start.getTime() + i * 60000);
      slots.push(time);
    }
    return slots;
  };
  const timeSlots = generateTimeSlots();

  // Convert time string to minutes for width calculation
  const timeToMinutes = (timeStr: string) => {
    const [time, period] = timeStr.split(/(am|pm)/);
    const [hoursStr, minutesStr] = time.split(':');
    let hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (period === 'pm' && hours !== 12) hours += 12;
    if (period === 'am' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Calculate program width based on duration
  const calculateProgramWidth = (start: string, end: string) => {
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);
    const durationMinutes = endMinutes - startMinutes;
    return (durationMinutes / 30) * 100; // 100px per 30 minutes
  };

  // Calculate program offset from 10am
  const calculateProgramOffset = (start: string) => {
    const startMinutes = timeToMinutes(start);
    const baseMinutes = 10 * 60; // 10am in minutes
    const offsetMinutes = startMinutes - baseMinutes;
    return (offsetMinutes / 30) * 100; // 100px per 30 minutes
  };

  // Scroll to focused program
  const scrollToFocusedProgram = (
    channelIndex: number,
    programIndex: number
  ) => {
    if (!containerRef.current) return;
    const program = channels[channelIndex]?.programs[programIndex];
    if (!program) return;
    const programOffset = calculateProgramOffset(program.start);
    const programWidth = calculateProgramWidth(program.start, program.end);
    const containerWidth = containerRef.current.offsetWidth - 100;
    const programLeft = programOffset;
    const programRight = programOffset + programWidth;
    const viewportLeft = scrollOffset;
    const viewportRight = scrollOffset + containerWidth;
    let newScrollOffset = scrollOffset;
    if (programRight > viewportRight) {
      newScrollOffset = programRight - containerWidth + 20;
    } else if (programLeft < viewportLeft) {
      newScrollOffset = programLeft - 20;
    }
    newScrollOffset = Math.max(0, newScrollOffset);
    setScrollOffset(newScrollOffset);
  };

  // Keyboard navigation handlers
  const handleUp = () => {
    setFocusedProgram((prev) => ({
      channelIndex: Math.max(0, prev.channelIndex - 1),
      programIndex: prev.programIndex,
    }));
  };
  const handleDown = () => {
    setFocusedProgram((prev) => ({
      channelIndex: Math.min(channels.length - 1, prev.channelIndex + 1),
      programIndex: prev.programIndex,
    }));
  };
  const handleLeft = () => {
    setFocusedProgram((prev) => ({
      channelIndex: prev.channelIndex,
      programIndex: Math.max(0, prev.programIndex - 1),
    }));
  };
  const handleRight = () => {
    setFocusedProgram((prev) => ({
      channelIndex: prev.channelIndex,
      programIndex: Math.min(
        (channels[prev.channelIndex]?.programs.length || 1) - 1,
        prev.programIndex + 1
      ),
    }));
  };
  const handleOk = () => {
    // Optionally handle selection
  };
  const handleBack = () => {
    // Optionally handle back
  };

  useMappedKeydown({
    isActive: true,
    onUp: handleUp,
    onDown: handleDown,
    onLeft: handleLeft,
    onRight: handleRight,
    onOk: handleOk,
    onBack: handleBack,
  });

  useEffect(() => {
    scrollToFocusedProgram(
      focusedProgram.channelIndex,
      focusedProgram.programIndex
    );
  }, [focusedProgram, channels]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const getCurrentTimePosition = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    if (hours >= 10 && hours < 14) {
      const totalMinutes = (hours - 10) * 60 + minutes;
      return (totalMinutes / 30) * 100;
    }
    return -1;
  };
  const currentTimePosition = getCurrentTimePosition();

  const getLogoStyle = (color: string) => {
    const colorMap: Record<string, React.CSSProperties> = {
      white: { backgroundColor: '#ffffff', color: '#000000' },
      blue: { backgroundColor: '#3b82f6', color: '#ffffff' },
      red: { backgroundColor: '#ef4444', color: '#ffffff' },
      yellow: { backgroundColor: '#eab308', color: '#000000' },
      orange: { backgroundColor: '#f97316', color: '#ffffff' },
      teal: { backgroundColor: '#14b8a6', color: '#ffffff' },
      darkred: { backgroundColor: '#dc2626', color: '#ffffff' },
    };
    return colorMap[color] || colorMap.white;
  };

  // Defensive: fallback if no channels or programs
  // Clamp indices to valid ranges
  const validChannelIndex = Math.max(
    0,
    Math.min(focusedProgram.channelIndex, channels.length - 1)
  );
  const currentChannel = channels[validChannelIndex] || { programs: [] };
  const validProgramIndex = Math.max(
    0,
    Math.min(
      focusedProgram.programIndex,
      (currentChannel.programs?.length || 1) - 1
    )
  );
  const currentProgram = currentChannel.programs[validProgramIndex] || {
    title: '',
    start: '',
    end: '',
  };

  // Reset focus if data changes and focus is out of bounds
  React.useEffect(() => {
    if (
      focusedProgram.channelIndex >= channels.length ||
      focusedProgram.channelIndex < 0 ||
      focusedProgram.programIndex >=
        (channels[focusedProgram.channelIndex]?.programs.length || 0) ||
      focusedProgram.programIndex < 0
    ) {
      setFocusedProgram({ channelIndex: 0, programIndex: 0 });
    }
  }, [channels]);

  return (
    <div className="epg-container">
      <div className="epg-maxWidth">
        <div className="epg-header">
          <h1 className="epg-title">TV Guide</h1>
          <div className="epg-dateTime">
            {currentTime.toLocaleDateString()} •{' '}
            {currentTime.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
        <div className="epg-timelineContainer" ref={containerRef}>
          <div className="epg-timeHeader">
            <div className="epg-timeHeaderSpacer"></div>
            <div className="epg-timeHeaderContent">
              <div
                className="epg-timeSlots"
                style={{ transform: `translateX(-${scrollOffset}px)` }}
              >
                {timeSlots.map((time, index) => (
                  <div key={index} className="epg-timeSlot">
                    {time.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                ))}
              </div>
              {currentTimePosition >= 0 && (
                <div
                  className="epg-currentTimeIndicator"
                  style={{
                    transform: `translateX(${
                      currentTimePosition - scrollOffset
                    }px)`,
                  }}
                >
                  <div className="epg-currentTimeDot"></div>
                </div>
              )}
            </div>
          </div>
          <div className="epg-channelsContainer" ref={timelineRef}>
            {channels.map((channel, channelIndex) => (
              <div key={channel.id} className="epg-channelRow">
                <div className="epg-channelLogo">
                  <div
                    className="epg-logo"
                    style={getLogoStyle(channel.logoColor)}
                  >
                    <img
                      src={channel.logo}
                      alt={`${channel.name} logo`}
                      className="epg-logo-image"
                    />
                  </div>
                </div>
                <div className="epg-programsTimeline">
                  <div
                    className="epg-programsContainer"
                    style={{ transform: `translateX(-${scrollOffset}px)` }}
                  >
                    {channel.programs.map((program, programIndex) => {
                      const width = calculateProgramWidth(
                        program.start,
                        program.end
                      );
                      const offset = calculateProgramOffset(program.start);
                      const isFocused =
                        focusedProgram.channelIndex === channelIndex &&
                        focusedProgram.programIndex === programIndex;
                      return (
                        <div
                          key={program.id}
                          className={`epg-program${
                            isFocused ? ' epg-program--focused' : ''
                          }`}
                          style={{ left: `${offset}px`, width: `${width}px` }}
                          onClick={() =>
                            setFocusedProgram({ channelIndex, programIndex })
                          }
                        >
                          <div className="epg-programContent">
                            <div className="epg-programTitle">
                              {program.title}
                            </div>
                            <div className="epg-programTime">
                              {program.start} - {program.end}
                            </div>
                          </div>
                          {isFocused && <Play className="epg-playIcon" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="epg-programDetails">
          <h3 className="epg-programDetailsTitle">Now Playing</h3>
          <div className="epg-programDetailsContent">
            {currentProgram.title}
          </div>
          <div className="epg-programDetailsTime">
            {currentProgram.start} - {currentProgram.end}
          </div>
        </div>
        <div className="epg-instructions">
          Use arrow keys to navigate • Press Enter to select • Shows current
          time with red indicator • Auto-scrolls to keep focused program visible
        </div>
      </div>
    </div>
  );
};
